// src/components/BoostDashboard/BoostDashboard.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  LinearProgress,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  alpha,
  useTheme,
} from '@mui/material';
import {
  TrendingUpRounded,
  StarRounded,
  VisibilityRounded,
  PhoneRounded,
  MoreVertRounded,
  RefreshRounded,
  CancelRounded,
  AccessTimeRounded,
  LocationOnRounded,
  RocketLaunchRounded,
} from '@mui/icons-material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';

const BoostDashboard = ({ onBoostProperty }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const [boosts, setBoosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: '', boost: null });
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedBoostForMenu, setSelectedBoostForMenu] = useState(null);

  useEffect(() => {
    loadActiveBoosts();
  }, []);

  const loadActiveBoosts = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_BASE_URL}/api/boost/my-boosts/`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setBoosts(response.data.results || response.data || []);
    } catch (error) {
      console.error('Erro ao carregar boosts:', error);
      setError('Erro ao carregar impulsionamentos ativos');
    } finally {
      setLoading(false);
    }
  };

  const handleRenewBoost = async (boost) => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API_BASE_URL}/api/boost/${boost.id}/renew/`, {
        payment_method: 'pix',
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      setConfirmDialog({ open: false, type: '', boost: null });
      navigate(`/checkout/${response.data.payment.id}`);
    } catch (error) {
      console.error('Erro ao renovar boost:', error);
      setError(error.response?.data?.error || 'Erro ao renovar impulsionamento');
    }
  };

  const handleCancelBoost = async (boost) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(`${API_BASE_URL}/api/boost/${boost.id}/cancel/`, {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      loadActiveBoosts();
      setConfirmDialog({ open: false, type: '', boost: null });
    } catch (error) {
      console.error('Erro ao cancelar boost:', error);
      setError('Erro ao cancelar impulsionamento');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'expiring': return 'warning';
      case 'expired': return 'error';
      case 'cancelled': return 'default';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return 'Ativo';
      case 'expiring': return 'Expirando';
      case 'expired': return 'Expirado';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const getRemainingTime = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const diff = end - now;
    
    if (diff <= 0) return 'Expirado';
    
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    
    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  const getProgressPercentage = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const now = new Date();
    
    const total = end - start;
    const elapsed = now - start;
    
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleMenuClick = (event, boost) => {
    setMenuAnchor(event.currentTarget);
    setSelectedBoostForMenu(boost);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedBoostForMenu(null);
  };

  const openConfirmDialog = (type, boost) => {
    setConfirmDialog({ open: true, type, boost });
    handleMenuClose();
  };

  if (loading) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Carregando dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Header Compacto */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <RocketLaunchRounded color="primary" sx={{ fontSize: 28 }} />
            <Typography variant="h5" component="h1" fontWeight="bold">
              Impulsionamentos
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Gerencie seus anúncios impulsionados
          </Typography>
        </Box>

        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshRounded />}
          onClick={loadActiveBoosts}
        >
          Atualizar
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Cards de Resumo - Compactos */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Ativos
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="primary">
                    {boosts.filter(b => b.status === 'active').length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>
                  <TrendingUpRounded />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ bgcolor: alpha(theme.palette.warning.main, 0.05) }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Expirando
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="warning.main">
                    {boosts.filter(b => b.status === 'expiring').length}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main', width: 40, height: 40 }}>
                  <AccessTimeRounded />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ bgcolor: alpha(theme.palette.success.main, 0.05) }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Visualizações
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="success.main">
                    {boosts.reduce((total, boost) => total + (boost.analytics?.views || 0), 0)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                  <VisibilityRounded />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 6, md: 3 }}>
          <Card sx={{ bgcolor: alpha(theme.palette.info.main, 0.05) }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Contatos
                  </Typography>
                  <Typography variant="h5" fontWeight="bold" color="info.main">
                    {boosts.reduce((total, boost) => total + (boost.analytics?.contacts || 0), 0)}
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                  <PhoneRounded />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tabela Compacta */}
      <Card>
        <CardContent sx={{ p: 2 }}>
          {boosts.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <RocketLaunchRounded sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Nenhum impulsionamento ativo
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Comece a impulsionar seus imóveis para ganhar mais visibilidade
              </Typography>
              <Button
                variant="contained"
                startIcon={<TrendingUpRounded />}
                onClick={() => onBoostProperty && onBoostProperty()}
              >
                Impulsionar Primeiro Imóvel
              </Button>
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Imóvel</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Plano</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Tempo Restante</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Investimento</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {boosts.map((boost) => (
                    <TableRow key={boost.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Box
                            component="img"
                            sx={{
                              width: 50,
                              height: 50,
                              objectFit: 'cover',
                              borderRadius: 1,
                            }}
                            src={boost.property.main_image || '/placeholder-property.jpg'}
                            alt={boost.property.title}
                          />
                          <Box>
                            <Typography variant="body2" fontWeight="medium" noWrap sx={{ maxWidth: 180 }}>
                              {boost.property.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              <LocationOnRounded sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                              {boost.property.city}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Chip
                          icon={boost.plan.plan_type === 'premium' ? <StarRounded /> : <TrendingUpRounded />}
                          label={boost.plan.name}
                          color={boost.plan.plan_type === 'premium' ? 'warning' : 'primary'}
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={getStatusText(boost.status)}
                          color={getStatusColor(boost.status)}
                          size="small"
                        />
                      </TableCell>

                      <TableCell>
                        <Box sx={{ minWidth: 100 }}>
                          <Typography variant="body2" fontWeight="medium">
                            {getRemainingTime(boost.end_date)}
                          </Typography>
                          <LinearProgress
                            variant="determinate"
                            value={100 - getProgressPercentage(boost.start_date, boost.end_date)}
                            color={boost.status === 'expiring' ? 'warning' : 'primary'}
                            sx={{ mt: 0.5, height: 4, borderRadius: 2 }}
                          />
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {formatPrice(boost.plan.price)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {boost.plan.duration_days} dias
                        </Typography>
                      </TableCell>

                      <TableCell align="center">
                        <IconButton
                          onClick={(e) => handleMenuClick(e, boost)}
                          size="small"
                        >
                          <MoreVertRounded />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Menu de Ações */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        <MenuItem 
          onClick={() => openConfirmDialog('renew', selectedBoostForMenu)}
          disabled={selectedBoostForMenu?.status === 'cancelled'}
        >
          <RefreshRounded sx={{ mr: 1, fontSize: 20 }} />
          Renovar
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => openConfirmDialog('cancel', selectedBoostForMenu)}
          disabled={selectedBoostForMenu?.status === 'cancelled'}
          sx={{ color: 'error.main' }}
        >
          <CancelRounded sx={{ mr: 1, fontSize: 20 }} />
          Cancelar
        </MenuItem>
      </Menu>

      {/* Dialog de Confirmação */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, type: '', boost: null })}
        maxWidth="xs"
      >
        <DialogTitle>
          {confirmDialog.type === 'renew' ? 'Renovar Impulsionamento' : 'Cancelar Impulsionamento'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            {confirmDialog.type === 'renew' 
              ? `Deseja renovar o impulsionamento do imóvel "${confirmDialog.boost?.property.title}" pelo mesmo plano?`
              : `Tem certeza que deseja cancelar o impulsionamento do imóvel "${confirmDialog.boost?.property.title}"?`
            }
          </Typography>
          {confirmDialog.type === 'renew' && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Valor: {formatPrice(confirmDialog.boost?.plan.price)} por {confirmDialog.boost?.plan.duration_days} dias
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ open: false, type: '', boost: null })}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color={confirmDialog.type === 'renew' ? 'primary' : 'error'}
            onClick={() => {
              if (confirmDialog.type === 'renew') {
                handleRenewBoost(confirmDialog.boost);
              } else {
                handleCancelBoost(confirmDialog.boost);
              }
            }}
          >
            {confirmDialog.type === 'renew' ? 'Renovar' : 'Confirmar Cancelamento'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BoostDashboard;